import {Injectable, NgZone} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {select, Store} from '@ngrx/store';
import {TreeConfig} from '@spout/any-shared/models';
import {
  accountSaveFirebase,
  genreMusicianAggregate,
  genreMusicianAggregateEffect,
  upsertWebsocketRegistry,
  websocketIsDisconnectedAction
} from '@spout/web-global/actions';
import {
  difference,
  dispatchNgrxFirestoreAggregate,
  firestoreGetMusicianGenrePath,
  firestoreGetMusicianGenresPath
} from '@spout/web-global/fns';
import {
  AuthAccountStates,
  AuthAccountStatesConnect,
  genresMusicianFeatureKey,
  GenresMusicianState
} from '@spout/web-global/models';
import {GetAuthAccountConnect, selectAllGenresMusician, selectAuthAccountConnect$} from '@spout/web-global/selectors';
import {onSnapshot, QuerySnapshot, WriteBatch} from 'firebase/firestore';
import {from, Observable} from 'rxjs';
import {map, mergeMap, take} from 'rxjs/operators';
import {SptFirestoreService} from '../firestore';

@Injectable()
export class GenresMusicianEffects {
  saveGenresToFirestore = createEffect(
    () =>
      this.actions$.pipe(
        ofType(genreMusicianAggregateEffect),
        map(action => {
          this.saveToFirebase(action.genres).subscribe(
            () => {
              this.zone.run(() => {
                this.store.dispatch(
                  accountSaveFirebase({
                    payload: {
                      hasGenres: true
                    }
                  })
                );
              });
            },
            () => {
              /* noop */
            }
          );
        })
      ),
    {dispatch: false}
  );

  private _databaseSub: (() => void) | undefined;

  constructor(
    private actions$: Actions,
    private store: Store<AuthAccountStatesConnect & GenresMusicianState>,
    private sptFirestore: SptFirestoreService,
    private zone: NgZone
  ) {
    const that = this;
    this.zone.run(() => {
      that.store.dispatch(
        upsertWebsocketRegistry({
          id: genresMusicianFeatureKey
        })
      );
    });

    this.store.pipe(selectAuthAccountConnect$).subscribe((s: AuthAccountStatesConnect) => {
      if (s.doConnect) {
        this.onConnect.call(this, s.user);
      } else {
        this.onDisconnect.call(this, s.user);
      }
    });
  }

  onConnect(user: AuthAccountStates): void {
    const that = this;
    if (!this._databaseSub) {
      this.zone.run(() => {
        that.store.dispatch(
          websocketIsDisconnectedAction({
            id: genresMusicianFeatureKey
          })
        );
      });

      this._databaseSub = onSnapshot(
        this.sptFirestore.collectionRef(firestoreGetMusicianGenresPath(user)),
        (snapshot: QuerySnapshot) => {
          dispatchNgrxFirestoreAggregate<AuthAccountStatesConnect>(
            this.store,
            snapshot,
            genreMusicianAggregate,
            this.zone
          );
        },
        (err: Error) => {
          console.error(err);
        },
        () => {
          /* noop */
        }
      );
    }
  }

  onDisconnect(user: AuthAccountStates): void {
    const that = this;
    if (this._databaseSub) {
      this._databaseSub();

      that.zone.run(() => {
        that.store.dispatch(
          websocketIsDisconnectedAction({
            id: genresMusicianFeatureKey
          })
        );
      });
    }
  }

  saveToFirebase(toSave: TreeConfig[]): Observable<any> {
    return this.store.pipe(
      select(selectAllGenresMusician),
      take(1),
      mergeMap((selected: TreeConfig[]) =>
        this.store.pipe(
          selectAuthAccountConnect$,
          map((s: GetAuthAccountConnect) => ({
            user: s.user
          })),
          mergeMap(({user}) => {
            const batch: WriteBatch = this.sptFirestore.writeBatch();

            // If user has no selected genres
            if (!selected.length) {
              toSave.map((genre: TreeConfig) => {
                const doc = this.sptFirestore.docRef(firestoreGetMusicianGenrePath(user, genre.id));
                batch.set(doc, genre);
              });
            } else {
              // TreeConfig[]
              // Add Genres
              difference(toSave, selected, 'id').map((genre: TreeConfig) => {
                const doc = this.sptFirestore.docRef(firestoreGetMusicianGenrePath(user, genre.id));
                batch.set(doc, genre);
              });

              // TreeConfig[]
              // Delete Genres that are not in selectedGenres
              difference(selected, toSave, 'id').map((genre: TreeConfig) => {
                const doc = this.sptFirestore.docRef(firestoreGetMusicianGenrePath(user, genre.id));
                batch.delete(doc);
              });
            }

            return from(batch.commit());
          })
        )
      )
    );
  }
}
